'use client';
/**/
import { YellowButton } from '@/components/button/button';
import { Box, Container, GlobalStyles, Grid, Link, styled, Typography } from '@mui/material';
import React, { useEffect, useState, type ComponentType } from 'react';
import dynamic from 'next/dynamic';
import Carousel from '../header-carousel/header-carousel';
// This imports the JSON
import shootingStar2 from './media/shooting-star-green.json';
import shootingStar3 from './media/shooting-star-orange.json';
import shootingStar4 from './media/shooting-star-yellow.json';
import twinkleStar1 from './media/star-twinkle-1.json';
import twinkleStar2 from './media/star-twinkle-2.json';
import twinkleStar3 from './media/star-twinkle-3.json';
import twinkleStar4 from './media/star-twinkle-4.json';
import ScrollDown from './components/scroll-down/scroll-down';
import { Stack } from '@mui/system';
import { config } from '@/config/environment';

type LottieProps = {
  options: Record<string, unknown>;
  speed?: number;
  eventListeners?: Array<{ eventName: string; callback: () => void }>;
};

const LottieBase = dynamic(
  () => import('react-lottie').then(mod => mod.default as unknown as ComponentType<LottieProps>),
  { ssr: false },
);
const Lottie = (props: LottieProps) => <LottieBase eventListeners={[]} {...props} />;

const HeaderBox = styled(Box)`
  height: auto;
  min-height: 80vh;
  margin: 0;
  position: relative;
  overflow: hidden;
  background-image: url('/home/horizon-desktop.png');
  background-size: cover;
  background-position: center;
  color: white;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;

  // Tablet screens
  ${({ theme }) => theme.breakpoints.down('md')} {
    background-image: url('/home/horizon-tablet.png');
    min-height: 100vh;
  }

  // Mobile  screens
  ${({ theme }) => theme.breakpoints.down('sm')} {
    background-image: url('/home/horizon-mobile.png');
    min-height: 100vh;
  }

  & a {
    :hover {
      text-decoration: none;
    }
  }

  & .twinkle-star {
    position: absolute;
    width: 20px;
    height: 20px;
    z-index: 0; /* Ensure stars are behind other elements */
  }

  & .shooting-star {
    position: absolute;
    width: 50px;
    height: 50px;
    z-index: 0; /* Ensure stars are behind other elements */
  }
`;

const ShareMomentsHeader = styled(Typography)`
  padding-top: 10vh;
  font-family: var(--font-cooper-light), sans-serif;
  font-weight: 300;
  font-size: 60px;
  line-height: 1.1;
  color: #ffffff;
  text-align: start;
  z-index: 1;
  margin-bottom: 32px;
  text-shadow:
    0 0 20px rgba(31, 21, 33),
    0 0 20px rgba(31, 21, 33),
    0 0 20px rgba(31, 21, 33);

  // Mobile screens
  @media (max-width: 768px) {
    width: 100%;
    padding-top: 5;
    font-size: 45px;
    text-align: center;
  }
`;
const ShareMomentsP = styled(Typography)`
  font-family: var(--font-work-sans), sans-serif;
  font-size: 18px;
  line-height: 160%;
  color: #ffffff;
  text-align: start;
  margin-bottom: 32px;
  width: 100%;
  max-width: 500px;
  text-shadow:
    0 0 20px rgba(31, 21, 33),
    0 0 20px rgba(31, 21, 33),
    0 0 20px rgba(31, 21, 33);
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const MobileCarouselContainer = styled('div')`
  display: none;
  padding: 0;

  // Mobile screens
  @media (max-width: 768px) {
    display: block;
    margin-bottom: 80px;
    margin-top: -50px;
  }
`;

const DesktopCarouselContainer = styled('div')`
  display: block;

  // Mobile screens
  @media (max-width: 768px) {
    display: none;
  }
`;

const OutlineButton = styled('a')`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  border: 2px solid #fff3dc;
  color: #fff3dc;
  font-family: var(--font-work-sans), sans-serif;
  font-weight: 600;
  font-size: 17px;
  padding: 12px 28px;
  text-decoration: none;
  transition: background-color 0.2s;
  white-space: nowrap;
  &:hover {
    background-color: rgba(255, 243, 220, 0.1);
    text-decoration: none;
  }
`;

const starAnimations = [twinkleStar1, twinkleStar2, twinkleStar3, twinkleStar4];
const shootingStarAnimations = [shootingStar2, shootingStar3, shootingStar4];

export const Header: React.FC = () => {
  const [stars, setStars] = useState<JSX.Element[]>([]);
  const archiveMode = config.platformMode === 'archive';

  const twinkleOptions = (animationData: unknown, speed: number) => ({
    loop: true,
    autoplay: true,
    animationData,
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid slice',
    },
    animationSpeed: speed,
  });

  const shootingOptions = (animationData: unknown) => ({
    loop: false,
    autoplay: true,
    animationData,
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid slice',
    },
  });

  useEffect(() => {
    const generateStars = (count: number) => {
      for (let i = 0; i < count; i++) {
        const top = Math.random() * 78; // Ensure stars are within 78% from the top
        const left = Math.random() * 100;
        const speed = 0.5 + Math.random() * 1.5; // Random speed between 0.5 and 2
        const delay = Math.random() * 1000; // Random delay up to 1s
        // Get random star
        const randomStar = starAnimations[Math.floor(Math.random() * starAnimations.length)];
        // Random scale between 0.2 and 0.4
        const scale = 0.2 + Math.random() * 0.35;

        setTimeout(() => {
          setStars(prevStars => [
            ...prevStars,
            <div
              key={`twinkle-${prevStars.length}`}
              className="twinkle-star"
              style={{
                top: `${top}%`,
                left: `${left}%`,
                transform: `scale(${scale})`,
                opacity: 0.5 + Math.random() * 0.5,
              }}
            >
              <Lottie speed={6} options={twinkleOptions(randomStar, speed)} />
            </div>,
          ]);
        }, delay);
      }
    };

    const generateShootingStars = (count: number) => {
      for (let i = 0; i < count; i++) {
        const top = Math.random() * 40; // Ensure stars are within 40vh from the top
        const left = Math.random() * 100; // Ensure stars are within 100vw from the left
        const randomShootingStar =
          shootingStarAnimations[Math.floor(Math.random() * shootingStarAnimations.length)];

        setStars(prevStars => [
          ...prevStars,
          <div
            key={`shooting-${prevStars.length}`}
            className="shooting-star"
            style={{
              top: `${top}%`,
              left: `${left}%`,
              transform: 'scale(3)',
              animation: 'fadeInOut 3s forwards',
            }}
          >
            <Lottie speed={0.1} options={shootingOptions(randomShootingStar)} />
          </div>,
        ]);
      }
    };

    const shootingStarInterval = setInterval(() => {
      generateShootingStars(2); // Generate 2 shooting stars every interval
    }, 10000); // Shooting stars appear every 10 seconds

    generateStars(25);
    generateShootingStars(2);

    return () => clearInterval(shootingStarInterval);
  }, []);

  return (
    <Stack width="100%" justifyContent="center" alignItems="center">
      <HeaderBox justifyContent="center">
        <GlobalStyles
          styles={{
            '@keyframes fadeOut': {
              '0%': {
                opacity: 1,
              },
              '100%': {
                opacity: 0,
              },
            },
            '@keyframes fadeInOut': {
              '0%': {
                opacity: 0,
              },
              '20%': {
                opacity: 1,
              },
              '35%': {
                opacity: 1,
              },
              '100%': {
                opacity: 0,
              },
            },
          }}
        />
        {/* Twinkling Stars */}
        {stars}
        <Container
          maxWidth="lg"
          sx={{
            mx: 'auto',
            minHeight: '100px',
            marginBottom: {
              xs: '100px',
              sm: '200px',
            },
            position: 'relative',
            zIndex: 99,
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <ShareMomentsHeader>
                {archiveMode
                  ? 'Celebrating the Curiko community'
                  : 'Spark curiosity, grow connections'}
              </ShareMomentsHeader>
              <MobileCarouselContainer>
                <Carousel />
              </MobileCarouselContainer>
              <ShareMomentsP>
                {archiveMode
                  ? `Curiko enabled people with and without disabilities to connect based on shared interests and passions. Anyone could host, and anyone could join in-person and online experiences. Check out the platform to see how experiences, coaching, peer roles, and supporting structures created opportunities for agency, belonging, and contribution.`
                  : `Welcome to Curiko! Join a community of people with and without disabilities and access free online and in-person experiences.`}
              </ShareMomentsP>
              <Box
                sx={{
                  display: {
                    xs: 'none',
                    sm: 'block',
                  },
                }}
              >
                <Stack direction="row" spacing={2} alignItems="center">
                  <Link href="/explore">
                    <YellowButton
                      label={archiveMode ? 'Past experiences' : 'Explore experiences'}
                    />
                  </Link>
                  {archiveMode && <OutlineButton href="#">Our learning journey</OutlineButton>}
                </Stack>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <DesktopCarouselContainer>
                <Carousel />
              </DesktopCarouselContainer>
            </Grid>
          </Grid>
        </Container>
      </HeaderBox>
      <Stack width="100%" justifyContent="center" alignItems="center">
        <Box
          sx={{
            display: {
              xs: 'block',
              sm: 'none',
            },
          }}
        >
          <Stack direction="column" spacing={2} alignItems="center">
            <Link href="/explore">
              <YellowButton label={archiveMode ? 'Past experiences' : 'Explore experiences'} />
            </Link>
            {archiveMode && <OutlineButton href="#">Our learning journey</OutlineButton>}
          </Stack>
        </Box>
        <ScrollDown />
      </Stack>
    </Stack>
  );
};
